/**
 * Customer PWA entitlement route guards (ENT-PR-19, plan §16.2).
 */
import { type ReactNode, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router-dom';
import { Button } from '../../shared/ui/Button.js';
import {
  CUSTOMER_TENANT_INACTIVE_TEST_ID,
  isTenantInactiveError,
} from '../../shared/api/tenantInactive.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import {
  isIdentityOnlyEntitlementIdle,
  useCustomerSurfaceEntitlementQuery,
} from './customerEntitlementApi.js';
import { useCustomerAccessRevisionInvalidation } from './useCustomerAccessRevisionInvalidation.js';
import { usePublicDirectorySseConnected } from '../shops/hooks/PublicDirectorySseProvider.js';
import {
  evaluateIdentityPipeline,
  mapRecoveryModeToPath,
} from '../../navigation/identityPipeline.js';
import { resolvePostAuthFallbackPath } from '../../navigation/navigationEngine.js';
import { CustomerSubpageBackBar } from '../../shared/ui/CustomerSubpageBackBar.js';
import {
  getDeclaredProductCapabilityGridCell,
  isProductCapabilityActivable,
  productCapabilityIdForEntitlementBlock,
  type ClientAccessDenyKind,
} from 'pi-kiosk-shared';

/** Block keys enforced on customer PWA routes (subset of tenant catalog). */
export type CustomerEntitlementBlockKey =
  | 'surface_customer'
  | 'surface_kiosk'
  | 'product_vending'
  | 'donation'
  /** Package E — PRO create/resume only; not a whole-page paywall. */
  | 'donation_recurring'
  | 'customer_auth_pwa'
  | 'order_pickup_infrastructure'
  | 'immediate_self_pickup'
  | 'loyalty_program'
  | 'promotions_program'
  /** Package E — PRO new restock subscribe; unsubscribe stays core. */
  | 'restock_alerts';

export interface CustomerSurfaceEntitlementSnapshot {
  readonly revision: number;
  readonly blocks: Readonly<Record<CustomerEntitlementBlockKey, boolean>>;
  /** Explain-only Option A — never grants access; omit when AVAILABLE. */
  readonly blockDenyKinds?: Readonly<
    Partial<Record<CustomerEntitlementBlockKey, ClientAccessDenyKind>>
  >;
  /**
   * Package G — opaque commercial/access fingerprint (`ar1:` + 64 hex).
   * Additive optional; when it changes, clear request-failure cache (G-AC-11).
   */
  readonly accessRevision?: string;
  /**
   * Package G — commercial DENY keys when projection ENFORCE (authoritative).
   * Omitted under OFF/SHADOW / empty; never invent from SHADOW metadata.
   */
  readonly commercialDeniedBlockKeys?: readonly string[];
}

const DENY_BY_DEFAULT_BLOCKS: Readonly<Record<CustomerEntitlementBlockKey, boolean>> = {
  surface_customer: false,
  surface_kiosk: false,
  product_vending: false,
  donation: false,
  donation_recurring: false,
  customer_auth_pwa: false,
  order_pickup_infrastructure: false,
  immediate_self_pickup: false,
  loyalty_program: false,
  promotions_program: false,
  restock_alerts: false,
};

function resolveClientProductReadinessMode(): 'test' | 'production' {
  return process.env.NODE_ENV === 'test' ? 'test' : 'production';
}

function isCustomerBlockReadinessActivable(blockKey: CustomerEntitlementBlockKey): boolean {
  const capabilityId = productCapabilityIdForEntitlementBlock(blockKey);
  if (capabilityId === undefined) {
    return true;
  }
  const mode = resolveClientProductReadinessMode();
  // Grid `n_a` = capability not scoped to this surface — trust the surface-entitlement
  // snapshot (e.g. CAP-06 order_pickup_infrastructure for customer_mobile_shop).
  // Without this, PREPAY/scheduled-pickup probe never arms and find-order stays denied.
  const cell = getDeclaredProductCapabilityGridCell(capabilityId, 'customer', { mode });
  if (cell === 'n_a') {
    return true;
  }
  return isProductCapabilityActivable({
    capabilityId,
    surface: 'customer',
    mode,
  });
}

export interface UseCustomerSurfaceEntitlementResult {
  readonly snapshot: CustomerSurfaceEntitlementSnapshot | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isTenantInactive: boolean;
  readonly error: Error | null;
  readonly refetch: () => void;
  readonly isBlockEntitled: (blockKey: CustomerEntitlementBlockKey) => boolean;
  readonly areBlocksEntitled: (blockKeys: readonly CustomerEntitlementBlockKey[]) => boolean;
}

export function useCustomerSurfaceEntitlement(): UseCustomerSurfaceEntitlementResult {
  const tenantCode = useTenantCode();
  const directorySseConnected = usePublicDirectorySseConnected();
  const query = useCustomerSurfaceEntitlementQuery({ directorySseConnected });

  const snapshot = useMemo<CustomerSurfaceEntitlementSnapshot | null>(() => {
    if (query.data !== undefined) {
      return query.data;
    }
    return null;
  }, [query.data]);

  useCustomerAccessRevisionInvalidation(tenantCode, snapshot?.accessRevision);

  const isBlockEntitled = useCallback(
    (blockKey: CustomerEntitlementBlockKey): boolean => {
      if (snapshot === null) {
        return DENY_BY_DEFAULT_BLOCKS[blockKey];
      }
      // Missing/partial blocks must never throw during BottomNav render.
      const blocks = snapshot.blocks;
      if (blocks === undefined || blocks === null) {
        return DENY_BY_DEFAULT_BLOCKS[blockKey];
      }
      return blocks[blockKey] === true && isCustomerBlockReadinessActivable(blockKey);
    },
    [snapshot],
  );

  const areBlocksEntitled = useCallback(
    (blockKeys: readonly CustomerEntitlementBlockKey[]): boolean =>
      blockKeys.every((blockKey) => isBlockEntitled(blockKey)),
    [isBlockEntitled],
  );

  const error = query.error ?? null;

  return {
    snapshot,
    isLoading: query.isLoading,
    isError: query.isError,
    isTenantInactive: isTenantInactiveError(error),
    error,
    refetch: (): void => {
      void query.refetch();
    },
    isBlockEntitled,
    areBlocksEntitled,
  };
}

export type CustomerEntitlementGuardMode = 'inline-unavailable' | 'redirect-unavailable';

interface EntitlementRouteGuardProps {
  readonly blockKeys: readonly CustomerEntitlementBlockKey[];
  readonly mode?: CustomerEntitlementGuardMode;
  readonly children: ReactNode;
}

export function EntitlementRouteGuard({
  blockKeys,
  mode = 'inline-unavailable',
  children,
}: EntitlementRouteGuardProps): JSX.Element {
  const tenantCode = useTenantCode();
  const { areBlocksEntitled, isLoading, isError, isTenantInactive, refetch } =
    useCustomerSurfaceEntitlement();

  // G8 — identity-only hub: entitlement query is intentionally idle (no API tenant /
  // BAN invent home jwt). Allow platform hubs (e.g. `/orders/find`); nav still
  // deny-by-default via isBlockEntitled (no showFindOrder invent).
  if (isIdentityOnlyEntitlementIdle(tenantCode)) {
    return <>{children}</>;
  }

  // DEACTIVATED / non-ACTIVE tenant — durable org copy (G108/G120l/G136), not cryptic 403.
  if (isTenantInactive) {
    return <TenantInactiveScreen />;
  }

  // Backend down / network / 5xx — never treat as access-denied (that caused blank recovery loops).
  if (isError) {
    return <ServerUnavailableScreen onRetry={refetch} />;
  }

  const pipelineDecision = evaluateIdentityPipeline({
    routeMatched: true,
    entitlementsLoading: isLoading,
    entitlementsGranted: areBlocksEntitled(blockKeys),
    entitlementMode: mode,
    sessionHydrated: true,
    sessionPresent: true,
    accountMeReady: true,
    accountMeValid: true,
    oidcLandingPending: false,
  });

  if (!pipelineDecision.allowScreenInit) {
    if (pipelineDecision.recoveryMode !== undefined) {
      return (
        <Navigate
          to={mapRecoveryModeToPath(tenantCode, pipelineDecision.recoveryMode)}
          replace
        />
      );
    }
    return <RouteEntitlementLoadingFallback />;
  }

  return <>{children}</>;
}

function RouteEntitlementLoadingFallback(): JSX.Element {
  const { t } = useTranslation('customer');
  return (
    <main className="mx-auto flex min-h-[40vh] max-w-lg items-center justify-center p-6">
      <p className="text-sm text-[var(--color-on-surface-muted)]" role="status">
        {t('shared.loading')}
      </p>
    </main>
  );
}

/** Entitlement deny (org disabled this surface) — used by `/unavailable`. */
export function SurfaceUnavailableScreen(): JSX.Element {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const shopFallback = resolvePostAuthFallbackPath(tenantCode);

  return (
    <main
      className="mx-auto flex min-h-[50vh] max-w-lg flex-col gap-3 p-6"
      data-testid="customer-surface-unavailable"
    >
      <CustomerSubpageBackBar
        to={shopFallback}
        label={t('checkout.success.backToShop')}
        testId="customer-unavailable-hierarchical-back"
      />
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <h1 className="text-xl font-semibold text-[var(--color-on-surface)]">
          {t('entitlement.surface.unavailable.title')}
        </h1>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('entitlement.surface.unavailable.body')}
        </p>
      </div>
    </main>
  );
}

/**
 * Tenant DEACTIVATED / non-ACTIVE — uses existing recovery tenant copy (MS-REC-081).
 * Prefer durable org messaging over HTTP 403 / server-unavailable.
 */
export function TenantInactiveScreen(): JSX.Element {
  const { t } = useTranslation('customer');

  return (
    <main
      className="mx-auto flex min-h-[50vh] max-w-lg flex-col items-center justify-center gap-4 p-6 text-center"
      data-testid={CUSTOMER_TENANT_INACTIVE_TEST_ID}
      role="alert"
    >
      <h1 className="text-xl font-semibold text-[var(--color-on-surface)]">
        {t('retail.rec.tenant.title')}
      </h1>
      <p className="text-sm text-[var(--color-on-surface-muted)]">
        {t('retail.rec.tenant.description')}
      </p>
      <Link
        to="/"
        className="text-sm font-medium text-[var(--color-accent)] underline underline-offset-2"
      >
        {t('retail.rec.tenant.cta.tenant-select')}
      </Link>
    </main>
  );
}

/** Backend / network failure — always visible; never a blank outlet. */
export function ServerUnavailableScreen({
  onRetry,
}: {
  readonly onRetry?: () => void;
}): JSX.Element {
  const { t } = useTranslation('customer');

  return (
    <main
      className="mx-auto flex min-h-[50vh] max-w-lg flex-col items-center justify-center gap-4 p-6 text-center"
      data-testid="customer-server-unavailable"
      role="alert"
    >
      <h1 className="text-xl font-semibold text-[var(--color-on-surface)]">
        {t('entitlement.surface.serverUnavailable.title')}
      </h1>
      <p className="text-sm text-[var(--color-on-surface-muted)]">
        {t('entitlement.surface.serverUnavailable.body')}
      </p>
      {onRetry !== undefined ? (
        <Button intent="primary" size="md" onClick={onRetry}>
          {t('entitlement.surface.serverUnavailable.retry')}
        </Button>
      ) : null}
    </main>
  );
}
